import { PhoneOff } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Chip,
  Grad,
  WindowFrame,
  PhoneFrame,
  tint,
  type ExplainerDeck,
} from "@/components/suite/SuiteExplainer";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

const ACCENT = "#4E9CFF";

/* ───────────────────────  Outbound teaching visuals  ─────────────────────── */

const Avatar = ({
  initials,
  hue,
  size = "sm",
}: {
  initials: string;
  hue: string;
  size?: "sm" | "lg";
}) => (
  <span
    className={cn(
      "flex shrink-0 items-center justify-center rounded-full font-bold text-white",
      size === "lg" ? "h-16 w-16 text-lg" : "h-9 w-9 text-xs",
    )}
    style={{ background: `linear-gradient(135deg, ${hue}, ${tint(hue, 0.7)})` }}
  >
    {initials}
  </span>
);

/* Slide 1 — a live outbound call in progress. */
const WAVE = [7, 15, 24, 11, 30, 18, 34, 21, 9, 27, 14, 23, 8, 19, 29, 13, 20, 10];

const ActiveCallMock = () => (
  <PhoneFrame>
    <div className="px-5 pb-6 pt-8">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wider text-genie-textSecondary">
          Outbound call
        </span>
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
          style={{ backgroundColor: tint(ACCENT, 0.16), color: "#222B45" }}
        >
          <span
            className="h-1.5 w-1.5 rounded-full animate-pulse motion-reduce:animate-none"
            style={{ backgroundColor: ACCENT }}
          />
          Live
        </span>
      </div>

      <div className="mt-6 flex flex-col items-center text-center">
        <Avatar initials="RB" hue="#FF4DB8" size="lg" />
        <p className="mt-3 text-base font-bold text-[#222B45]">Rachel Bell</p>
        <p className="text-xs text-genie-textSecondary">Northside Ltd</p>
        <p className="mt-2 text-2xl font-extrabold tabular-nums text-[#222B45]">01:12</p>
      </div>

      <div
        className="mt-5 flex h-12 items-center justify-center gap-[3px] animate-pulse motion-reduce:animate-none"
        aria-hidden
      >
        {WAVE.map((h, i) => (
          <span
            key={i}
            className="w-[3px] rounded-full"
            style={{
              height: `${h}px`,
              backgroundColor: ACCENT,
              opacity: 0.45 + (h / 34) * 0.55,
            }}
          />
        ))}
      </div>

      <div className="mt-5 flex items-center justify-center gap-6">
        <div className="flex flex-col items-center gap-1">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F1F5F9]">
            <GenieIcon name="microphone" size={20} className="h-5 w-5" />
          </span>
          <span className="text-[10px] font-semibold text-genie-textSecondary">Mute</span>
        </div>
        <div className="flex flex-col items-center gap-1">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FF5F57] shadow-lg">
            <PhoneOff className="h-5 w-5 text-white" />
          </span>
          <span className="text-[10px] font-semibold text-genie-textSecondary">End</span>
        </div>
      </div>
    </div>
  </PhoneFrame>
);

/* Slide 2 — a streaming call transcript that adapts, ending in a next step. */
const GenieBubble = ({ text, adapt }: { text: string; adapt?: boolean }) => (
  <div className="flex items-end gap-2">
    <span
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
      style={{ backgroundColor: tint(ACCENT, 0.16) }}
    >
      <img src="/genie-mascots/role/sales.png" alt="" className="h-6 w-6 object-contain" />
    </span>
    <div
      className="max-w-[78%] rounded-2xl rounded-bl-sm px-3 py-2 text-[13px] leading-snug text-[#222B45]"
      style={{ backgroundColor: tint(ACCENT, 0.12) }}
    >
      {adapt && (
        <span
          className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide"
          style={{ color: ACCENT }}
        >
          Adapts
        </span>
      )}
      {text}
    </div>
  </div>
);

const ContactBubble = ({ initials, hue, text }: { initials: string; hue: string; text: string }) => (
  <div className="flex flex-row-reverse items-end gap-2">
    <Avatar initials={initials} hue={hue} />
    <div className="max-w-[78%] rounded-2xl rounded-br-sm bg-[#F1F5F9] px-3 py-2 text-[13px] leading-snug text-[#222B45]">
      {text}
    </div>
  </div>
);

const TranscriptMock = () => (
  <WindowFrame title="Live call · 01:12" accent={ACCENT} className="max-w-md">
    <div className="space-y-3">
      <GenieBubble text="Hi Rachel, it's Help Genie calling from Northside about your extension quote." />
      <ContactBubble initials="RB" hue="#FF4DB8" text="Oh right, yes. Can we push it to next week?" />
      <GenieBubble adapt text="Of course. I have Tuesday at 10 or Thursday at 2." />
      <ContactBubble initials="RB" hue="#FF4DB8" text="Thursday works." />
    </div>

    <div
      className="mt-4 flex items-center gap-3 rounded-xl border border-dashed border-[#E5EAF0] px-3 py-2.5"
      style={{ background: tint(ACCENT, 0.06) }}
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: tint(ACCENT, 0.14) }}
      >
        <GenieIcon name="calendar-check" size={18} className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0">
        <p className="text-[13px] font-bold text-[#222B45]">Next step: book callback</p>
        <p className="text-xs text-genie-textSecondary">Thursday 2:00pm</p>
      </div>
      <Chip accent={ACCENT} tone="solid" className="ml-auto shrink-0">
        CRM updated
      </Chip>
    </div>
  </WindowFrame>
);

/* Slide 3 — a whole campaign working itself through the list. */
type Outcome = "booked" | "voicemail" | "called" | "calling";

const OutcomeChip = ({ outcome }: { outcome: Outcome }) => {
  if (outcome === "booked")
    return (
      <Chip accent={ACCENT} tone="solid" className="shrink-0">
        Booked
      </Chip>
    );
  if (outcome === "voicemail")
    return (
      <Chip accent={ACCENT} tone="outline" className="shrink-0">
        Voicemail
      </Chip>
    );
  if (outcome === "calling")
    return (
      <Chip accent={ACCENT} className="shrink-0">
        <span
          className="h-1.5 w-1.5 rounded-full animate-pulse motion-reduce:animate-none"
          style={{ backgroundColor: ACCENT }}
        />
        Calling now
      </Chip>
    );
  return (
    <Chip accent={ACCENT} className="shrink-0">
      Called
    </Chip>
  );
};

const CampaignRow = ({
  initials,
  hue,
  name,
  company,
  outcome,
}: {
  initials: string;
  hue: string;
  name: string;
  company: string;
  outcome: Outcome;
}) => (
  <div
    className={cn(
      "flex items-center gap-3 rounded-xl px-3 py-2.5",
      outcome === "calling" ? "bg-white ring-2" : "bg-white/60",
    )}
    style={
      outcome === "calling"
        ? ({ "--tw-ring-color": tint(ACCENT, 0.6) } as React.CSSProperties)
        : undefined
    }
  >
    <Avatar initials={initials} hue={hue} />
    <div className="min-w-0 flex-1">
      <p className="truncate text-[13px] font-semibold text-[#222B45]">{name}</p>
      <p className="truncate text-xs text-genie-textSecondary">{company}</p>
    </div>
    <OutcomeChip outcome={outcome} />
  </div>
);

const CampaignMock = () => (
  <WindowFrame title="Campaign · Spring follow-ups" accent={ACCENT} className="max-w-md">
    <div className="mb-2 flex items-center justify-between">
      <span className="text-sm font-bold text-[#222B45]">18 of 40 done</span>
      <span className="text-xs text-genie-textSecondary">12 booked</span>
    </div>
    <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-[#EEF1F6]">
      <div
        className="h-full rounded-full"
        style={{ width: "45%", background: `linear-gradient(90deg, ${ACCENT}, #FF4DB8)` }}
      />
    </div>
    <div className="space-y-1.5">
      <CampaignRow initials="RB" hue="#FF4DB8" name="Rachel Bell" company="Northside Ltd" outcome="booked" />
      <CampaignRow initials="TG" hue="#4E9CFF" name="Tom Grady" company="Grady Auto" outcome="voicemail" />
      <CampaignRow initials="PN" hue="#FF4DB8" name="Priya Nair" company="Coastline Group" outcome="called" />
      <CampaignRow initials="JC" hue="#FFBB33" name="James Cole" company="Cole & Sons" outcome="calling" />
    </div>
  </WindowFrame>
);

/* Slide 4 — the campaign summary, handed back the moment it wraps. */
const StatTile = ({ genieIcon, value, label }: { genieIcon: GenieIconName; value: string; label: string }) => (
  <div className="rounded-xl border border-[#E5EAF0] bg-[#F9FAFC] px-2 py-3 text-center">
    <GenieIcon name={genieIcon} size={20} style={{ display: "block", margin: "0 auto" }} />
    <p className="mt-1.5 text-xl font-extrabold text-[#222B45]">{value}</p>
    <p className="text-[11px] leading-tight text-genie-textSecondary">{label}</p>
  </div>
);

const SummaryMock = () => (
  <WindowFrame title="Today's campaign" accent={ACCENT} className="max-w-sm">
    <div className="flex items-center gap-3">
      <span
        className="flex h-11 w-11 items-center justify-center rounded-xl"
        style={{ backgroundColor: tint(ACCENT, 0.14) }}
      >
        <img src="/genie-mascots/role/sales.png" alt="" className="h-8 w-8 object-contain" />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-bold text-[#222B45]">Spring follow-ups</p>
        <p className="text-xs text-genie-textSecondary">40 contacts, worked end to end</p>
      </div>
      <Chip accent={ACCENT} tone="solid" className="ml-auto shrink-0">
        Signing you in
      </Chip>
    </div>
    <div className="mt-4 grid grid-cols-3 gap-2">
      <StatTile genieIcon="phone" value="40" label="Calls made" />
      <StatTile genieIcon="calendar-check" value="12" label="Booked" />
      <StatTile genieIcon="clipboard-check" value="3" label="Next up" />
    </div>
  </WindowFrame>
);

/* ────────────────────────────────  Deck  ──────────────────────────────────── */

export const outboundDeck: ExplainerDeck = {
  appId: "outbound",
  enterLabel: "Enter Help Genie Outbound",
  slides: [
    {
      key: "hero",
      eyebrow: "What it is",
      title: (
        <>
          Your Genie picks up the phone <Grad>so you do not have to.</Grad>
        </>
      ),
      body: "Help Genie Outbound places real calls and runs whole campaigns end to end. Point your Genie at a contact list and it dials, holds a real conversation, and reports back.",
      visual: <ActiveCallMock />,
    },
    {
      key: "how",
      eyebrow: "How it works",
      title: (
        <>
          It dials, talks, and listens, then <Grad>reports back.</Grad>
        </>
      ),
      body: "Your Genie holds a real conversation on every call. It listens, adapts to what it hears, and hands you a transcript with a clear next step.",
      features: [
        {
          genieIcon: "phone",
          label: "Places live calls",
          sub: "Dials your list and speaks in a natural voice.",
        },
        {
          genieIcon: "microphone",
          label: "Listens and adapts",
          sub: "Follows the conversation and adjusts in real time.",
        },
        {
          genieIcon: "database",
          label: "Updates your CRM",
          sub: "Logs the transcript and the recommended next step.",
        },
      ],
      visual: <TranscriptMock />,
    },
    {
      key: "campaign",
      eyebrow: "The payoff",
      title: (
        <>
          A full campaign, <Grad>without the phone tag.</Grad>
        </>
      ),
      body: "Every call comes back logged and actioned. Booked, voicemail, or called back, so your time goes to the conversations that are ready to move.",
      visual: <CampaignMock />,
    },
    {
      key: "enter",
      eyebrow: "You are one click in",
      title: (
        <>
          Point it at a list and <Grad>let it run.</Grad>
        </>
      ),
      body: "The dialing, the talking, the follow-up notes, all handled in the background. What is left is a short list of warm callbacks. That is the whole idea. We are signing you in now.",
      visual: <SummaryMock />,
    },
  ],
};
